import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import Mustache from "mustache"

import FilterIcon from "shared/components/icons/filter_icon"
import ApiList from "shared/components/filter_code/api_list"
import { buildFilterContextDataForDIY } from "shared/components/filter_code/build_filter_context_data"
import ErrorMessageForField from "./error_message"
import { validateFilterCode } from "./api"
import { trackStateChangeFilterCodeCreated } from "./utils/telemetry"

import "./filter_code.scss"

const MonacoEditor = React.lazy(() => import("shared/components/monaco_editor"))

export default function FilterCode({
  contextTemplate,
  selectedTrigger,
  selectedQueries,
  selectedActions,
  filterCode,
  updateFilterCode,
}) {
  const contextData = buildFilterContextDataForDIY({
    selectedTrigger,
    selectedQueries,
    selectedActions,
  })

  const [code, updateCode] = useState(filterCode)
  const [errors, updateErrors] = useState([])
  const [submitDisabled, updateSubmitDisabled] = useState(false)

  useEffect(() => {
    if (errors.length) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [errors])

  const buttonText = () => {
    const verb = filterCode === "" ? "Creat" : "Updat"

    return submitDisabled ? `${verb}ing filter...` : `${verb}e filter`
  }

  const onClick = () => {
    updateSubmitDisabled(true)
    trackStateChangeFilterCodeCreated()

    validateFilterCode({
      filter_code: code,
      trigger: selectedTrigger,
      queries: selectedQueries,
      actions: selectedActions,
    })
      .then(({ errors }) => {
        if (errors.length) {
          updateErrors(errors)
          updateSubmitDisabled(false)
        } else {
          updateFilterCode(code)
        }
      })
      .catch(() => {
        updateErrors([{ message: "Unable to validate filter code" }])
        updateSubmitDisabled(false)
      })
  }

  return (
    <div styleName="filter-code" className="filter-code-editor">
      <span styleName="icon" className="hide-on-sm">
        <FilterIcon />
      </span>

      <div styleName="filter-container">
        <header>Filter Code</header>
        <p>
          Write JavaScript code to override action fields and skip actions. All
          actions will run unless you explicitly skip them. Check out{" "}
          <a
            href="https://ift.tt/filter-code"
            target="_blank"
            rel="noopener noreferrer"
            className="link-inline"
          >
            this help center article
          </a>{" "}
          for more information and examples.
        </p>

        {errors.map(e => (
          <ErrorMessageForField key={e.message} errors={e} field="message" />
        ))}
        <React.Suspense fallback="Loading...">
          <MonacoEditor
            positionAbsolute={false}
            defaultValue={code}
            onChange={updateCode}
            typescriptContext={Mustache.render(contextTemplate, contextData)}
          />
        </React.Suspense>
      </div>

      <ApiList contextData={contextData} />

      <div styleName="cta">
        <button
          className="button-primary"
          disabled={submitDisabled}
          onClick={onClick}
        >
          {buttonText()}
        </button>
      </div>
    </div>
  )
}

FilterCode.propTypes = {
  contextTemplate: PropTypes.string.isRequired,
  selectedTrigger: PropTypes.object,
  selectedQueries: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedActions: PropTypes.arrayOf(PropTypes.object).isRequired,
  filterCode: PropTypes.string,
  updateFilterCode: PropTypes.func.isRequired,
}

FilterCode.defaultProps = {
  filterCode: "",
}
